import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { api } from "@/lib/api";
import { SEOHead } from "@/components/SEO";
import VendorDetail from "@/pages/VendorDetail";
import VendorCategoryPage from "@/pages/seo/VendorCategoryPage";
import VendorCityPage from "@/pages/seo/VendorCityPage";

const isObjectId = (s) => typeof s === "string" && /^[0-9a-f]{24}$/i.test(s);

/**
 * <VendorSlugResolver /> — single React route that backs `/vendors/:slugOrId`.
 *
 * The same URL prefix can resolve to three different page types (vendor,
 * category, city) plus a legacy 24-hex id. We ask the backend resolver
 * once, then mount the right component. While we're waiting we render a
 * tiny loading state so SSR-style crawlers don't see a flash of empty body.
 */
export default function VendorSlugResolver() {
    const { slugOrId } = useParams();
    const navigate = useNavigate();
    const [resolved, setResolved] = useState(null); // {type, slug} or null
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let cancelled = false;
        // Legacy 24-hex id → let VendorDetail handle the lookup + redirect.
        if (isObjectId(slugOrId)) {
            setResolved({ type: "vendor", slug: slugOrId });
            return;
        }
        (async () => {
            try {
                const r = await api.get(`/seo/resolve/${slugOrId}`);
                if (cancelled) return;
                if (r.data.type === "not_found") {
                    setNotFound(true);
                } else {
                    setResolved(r.data);
                }
            } catch (err) {
                if (!cancelled) setNotFound(true);
            }
        })();
        return () => { cancelled = true; };
    }, [slugOrId, navigate]);

    if (notFound) {
        return (
            <Shell>
                <SEOHead title="Not found" noindex />
                <div className="max-w-2xl mx-auto px-6 py-24 text-center">
                    <h1 className="font-heading text-3xl font-extrabold">We couldn’t find that page</h1>
                    <p className="text-brand-mute mt-3">No vendor, category, or city matches <span className="font-mono">/{slugOrId}</span>.</p>
                    <Link to="/vendors" className="pill btn-forest px-5 py-3 font-semibold inline-block mt-6">Browse all vendors</Link>
                </div>
            </Shell>
        );
    }
    if (!resolved) return <Shell><div className="p-10 text-brand-mute">Loading…</div></Shell>;

    if (resolved.type === "vendor") return <VendorDetail />;
    if (resolved.type === "category") return <VendorCategoryPage />;
    if (resolved.type === "city") return <VendorCityPage />;
    return <Shell><div className="p-10 text-brand-mute">Unknown content type.</div></Shell>;
}
